import {
  type StanowiskoDoOceny,
  najnowszyKodWersji,
  ocenStanowisko,
  podsumujFlote,
} from '@insolitum/fabrica-panel-core';
import { Alert, EmptyState, Tabs, TabsContent, TabsList, TabsTrigger } from '@insolitum/ui';
import {
  Smartphone,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  KeyRound,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Unlink,
  PackageCheck,
  Pencil,
  Tv,
  Scale,
  LayoutGrid,
  TabletSmartphone,
  Factory,
  Server,
  Plug,
} from 'lucide-react';
import { useState, useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { DisplayDiagnostics } from '../../components/admin/DisplayDiagnostics';
import { FlotaPaneli } from '../../components/admin/FlotaPaneli';
import { PanelDiagnostics, type PanelPulsFacts } from '../../components/admin/PanelDiagnostics';
import { SHELL_EVENTS, publishShellEvent } from '../../lib/shellEvents';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

import { AgenciReceptora } from './devices/AgenciReceptora';
import { KartaAplikacji, type PozycjaDoPobrania } from './devices/KartaAplikacji';
import { PrzewodnikParowania } from './devices/PrzewodnikParowania';
import { SerweryDruku } from './devices/SerweryDruku';
import {
  KATALOG_WYDAN,
  type PozycjaWydania,
  type RodzinaUrzadzen,
  pozycjeRodziny,
  urlDoPobrania,
} from './devices/katalogWydan';
import { type RodzinaLicencji, policzRodziny, rodzinaLicencji } from './devices/kategorie';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

interface DeviceLicense {
  id: string;
  license_code: string;
  name: string | null;
  modules: string[];
  device_id: string | null;
  paired_at: string | null;
  status: string;
  expires_at: string | null;
  last_seen_at: string | null;
  last_heartbeat_at: string | null;
  created_at: string;
  /** Ustawienia ściany (board, czujniki, pogoda) — docs TV_DISPLAY_RUNTIME §2. */
  display_config: Record<string, unknown> | null;
  /**
   * What the device last reported about itself. Overwritten in place, so it is
   * always the current picture — never a series.
   *
   * TWO DIFFERENT SHAPES SHARE THIS COLUMN and that is the reason the panel's
   * telemetry went unread for so long. A wall-mounted screen writes under
   * `runtime` (`record_display_health`, migration 249); a production panel
   * merges its pulse at the TOP LEVEL (`device_info || p_puls`, migration 497),
   * so `runtime` stays undefined there and the display card silently renders
   * nothing. Which shape it is, is decided by `app === 'fabrica-panel'`.
   */
  device_info:
    | ({
        runtime?: {
          status?: string;
          observed_at?: string;
          board?: string;
          gateway?: string;
          overscan?: number;
          app?: string;
          screen?: { w?: number; h?: number; dpr?: number };
          viewport?: { w?: number; h?: number };
          native?: { app?: string; webview?: string; model?: string; android?: number } | null;
        };
      } & PanelPulsFacts)
    | null;
}

interface AppVersion {
  update_available: boolean;
  version_code?: number;
  version_name?: string | null;
  storage_path?: string;
  sha256?: string;
  changelog?: string | null;
}

interface Pairing {
  license: DeviceLicense;
  code: string;
  expires_at: string;
}

/**
 * Zakładki działu. „Przegląd" jest domyślna, bo pierwsze pytanie brzmi „czy
 * wszystko działa", a nie „co pobrać".
 */
const ZAKLADKI = [
  'przeglad',
  'telewizory',
  'mobilne',
  'stanowiska',
  'serwery',
  'integracje',
] as const;
type Zakladka = (typeof ZAKLADKI)[number];

function czytajZakladke(surowa: string | null): Zakladka {
  return (ZAKLADKI as readonly string[]).includes(surowa ?? '') ? (surowa as Zakladka) : 'przeglad';
}

export function DevicesPage() {
  const { currentOrganization } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const zakladka = czytajZakladke(searchParams.get('tab'));

  const ustawZakladke = (wartosc: string) => {
    const nastepne = new URLSearchParams(searchParams);
    nastepne.set('tab', wartosc);
    setSearchParams(nastepne, { replace: true });
  };

  const organizationId = currentOrganization?.organization_id;
  const [licenses, setLicenses] = useState<DeviceLicense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isPairing, setIsPairing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [copied, setCopied] = useState(false);
  const [newLicenseName, setNewLicenseName] = useState('');
  const [newLicenseModules, setNewLicenseModules] = useState<string[]>(['waterandice']);
  const [editingLicense, setEditingLicense] = useState<DeviceLicense | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [editModules, setEditModules] = useState<string[]>([]);
  const [editStatus, setEditStatus] = useState('active');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  /** Wybrane czujniki ekranu (device_id). Pusto = wszystkie (docs §2.2). */
  const [editSensors, setEditSensors] = useState<string[]>([]);
  const [orgSensors, setOrgSensors] = useState<Array<{ device_id: string; device_name: string }>>(
    []
  );
  const [showActivationGuide, setShowActivationGuide] = useState(false);
  /**
   * Wydania z rejestru, po jednym na pozycję KATALOGU_WYDAN.
   *
   * Jedna mapa zamiast osobnej zmiennej na rodzinę: rodzin jest dziś cztery
   * i każda następna wymagałaby kolejnego `useState` plus kolejnego wywołania
   * w efekcie niżej. Klucz mapy to `klucz` pozycji katalogu, nie nazwa
   * platformy — dwie rodziny mogą wydawać się na tę samą platformę.
   */
  const [wydania, setWydania] = useState<Record<string, AppVersion>>({});
  const [error, setError] = useState<string | null>(null);

  // Rejestr wydań odpowiada na JEDNĄ trójkę (aplikacja, platforma, kanał)
  // naraz — `app_versions` ma RLS bez polityk, więc nie da się go wylistować
  // (migracja 183). Pytamy więc raz per pozycja katalogu.
  useEffect(() => {
    let aktualne = true;
    const pobierz = async (pozycja: PozycjaWydania) => {
      const { data, error: vErr } = await supabase.rpc('get_app_version', {
        p_app: pozycja.app,
        p_platform: pozycja.platform,
        p_channel: 'stable',
        p_current: 0,
      });
      // `get_app_version` na brak wydania zwraca `{update_available:false}` —
      // obiekt PRAWDZIWY i pusty. Zapisujemy go mimo to, a odsiew robi dopiero
      // `urlDoPobrania` po obecności `storage_path`; inaczej pusty wpis jednej
      // platformy wygrywałby z niepustym wpisem drugiej.
      if (!vErr && data && aktualne) {
        setWydania((poprzednie) => ({ ...poprzednie, [pozycja.klucz]: data as AppVersion }));
      }
    };
    KATALOG_WYDAN.forEach((pozycja) => void pobierz(pozycja));
    return () => {
      aktualne = false;
    };
  }, []);

  const loadLicenses = async () => {
    if (!organizationId) return;

    setIsLoading(true);
    try {
      const { data, error: lErr } = await supabase
        .from('device_licenses')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (lErr) throw lErr;
      setLicenses(data || []);
    } catch (err) {
      console.error('Error loading licenses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Czujniki organizacji — do wyboru, KTÓRE z nich pokazuje dany ekran
   * (docs TV_DISPLAY_RUNTIME §2.2). Wybiera się po nazwie; do configu idzie
   * `device_id`, bo nazwę można zmienić, a wybór ma przetrwać zmianę nazwy.
   */
  const loadOrgSensors = async () => {
    if (!organizationId) return;
    const { data, error: sErr } = await supabase
      .from('neurosense_sensor_latest')
      .select('device_id, device_name')
      .eq('organization_id', organizationId)
      .order('device_name', { ascending: true });
    if (sErr) {
      // Brak listy czujników nie może zablokować edycji nazwy czy modułów —
      // sekcja wyboru po prostu się nie pokaże.
      console.error('Error loading sensors:', sErr);
      return;
    }
    setOrgSensors((data as Array<{ device_id: string; device_name: string }>) || []);
  };

  useEffect(() => {
    loadLicenses();
    void loadOrgSensors();
  }, [organizationId]);

  /**
   * Pozycje rodziny gotowe do pokazania: te, dla których rejestr ma wydanie
   * i da się złożyć adres pliku. Platforma bez wydania po prostu wypada —
   * przycisk do pliku, którego nie ma, byłby gorszy niż jego brak.
   */
  const doPobrania = (rodzina: RodzinaUrzadzen): PozycjaDoPobrania[] =>
    pozycjeRodziny(rodzina)
      .map((pozycja) => ({
        pozycja,
        wydanie: wydania[pozycja.klucz],
        url: urlDoPobrania(SUPABASE_URL, pozycja, wydania[pozycja.klucz]),
      }))
      .filter((p): p is PozycjaDoPobrania => Boolean(p.url && p.wydanie));

  /**
   * Numer wersji rodziny bierzemy z platformy wiodącej, a gdy jej wydanie
   * padło — z pierwszej, która się udała. Szukamy w LIŚCIE DO POBRANIA, a nie
   * w surowej mapie: pusta odpowiedź rejestru jest obiektem prawdziwym
   * i wygrałaby z niepustą resztą, pokazując „brak wydania" mimo wydanego
   * Windows.
   */
  const wersjaRodziny = (pozycje: PozycjaDoPobrania[]) =>
    (pozycje.find((p) => p.pozycja.glowna) ?? pozycje[0])?.wydanie;

  const urlKoduQr = (pozycje: PozycjaDoPobrania[]) =>
    pozycje.find((p) => p.pozycja.qr)?.url ?? null;

  const mobilne = doPobrania('mobilne');
  const telewizory = doPobrania('telewizory');
  const stanowiska = doPobrania('stanowiska');
  const serwery = doPobrania('serwery');
  const integracje = doPobrania('integracje');

  const createLicense = async () => {
    if (!organizationId || !newLicenseName) return;

    setIsCreating(true);
    setError(null);
    try {
      // Generate license code using database function
      const { data: codeData, error: codeError } = await supabase.rpc('generate_license_code');
      if (codeError) throw codeError;

      const { error: insError } = await supabase.from('device_licenses').insert({
        organization_id: organizationId,
        license_code: codeData,
        name: newLicenseName,
        modules: newLicenseModules,
        status: 'active',
      });

      if (insError) throw insError;

      // `license_code` is a pairing secret — it stays out of the payload.
      void publishShellEvent(SHELL_EVENTS.deviceRegistered, {
        name: newLicenseName,
        modules: newLicenseModules,
      });

      setShowCreateModal(false);
      setNewLicenseName('');
      loadLicenses();
    } catch (err) {
      console.error('Error creating license:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się dodać urządzenia.');
    } finally {
      setIsCreating(false);
    }
  };

  const openEditModal = (license: DeviceLicense) => {
    setError(null);
    setEditingLicense(license);
    setEditName(license.name || '');
    setEditModules(license.modules || []);
    setEditStatus(license.status || 'active');
    setEditExpiresAt(license.expires_at ? license.expires_at.slice(0, 10) : '');
    const configured = (license.display_config as { sensors?: unknown } | null)?.sensors;
    setEditSensors(Array.isArray(configured) ? (configured as string[]) : []);
  };

  const updateLicense = async () => {
    if (!editingLicense || !editName) return;

    setIsSaving(true);
    setError(null);
    try {
      const { error: uErr } = await supabase
        .from('device_licenses')
        .update({
          name: editName,
          modules: editModules,
          status: editStatus,
          expires_at: editExpiresAt ? new Date(editExpiresAt).toISOString() : null,
          // Scalamy, a nie nadpisujemy: display_config trzyma też board,
          // quiet_hours i overscan, których ten modal nie dotyka.
          display_config: {
            ...(editingLicense.display_config ?? {}),
            // Pusty wybór zapisujemy jako pustą listę = „wszystkie" (docs §2.2),
            // więc odznaczenie wszystkiego wraca do zachowania domyślnego.
            sensors: editSensors,
          },
        })
        .eq('id', editingLicense.id);

      if (uErr) throw uErr;

      void publishShellEvent(SHELL_EVENTS.deviceUpdated, {
        license_id: editingLicense.id,
        name: editName,
        modules: editModules,
        status: editStatus,
      });

      setEditingLicense(null);
      loadLicenses();
    } catch (err) {
      console.error('Error updating license:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się zapisać zmian.');
    } finally {
      setIsSaving(false);
    }
  };

  // Single 6-digit pairing code (BLIK-style) — no organization code needed.
  const generatePairingCode = async (license: DeviceLicense) => {
    setIsPairing(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('create_device_pairing_code', {
        p_license_id: license.id,
        p_ttl_minutes: 15,
      });
      if (rpcErr) throw rpcErr;
      const result = data as { code: string; expires_at: string };
      setPairing({ license, code: result.code, expires_at: result.expires_at });
      setCopied(false);
    } catch (err) {
      console.error('Error generating pairing code:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się wygenerować kodu parowania.');
    } finally {
      setIsPairing(false);
    }
  };

  const unpairDevice = async (license: DeviceLicense) => {
    if (!confirm('Czy na pewno chcesz odłączyć to urządzenie?')) return;

    try {
      const { error: uErr } = await supabase
        .from('device_licenses')
        .update({ device_id: null, paired_at: null })
        .eq('id', license.id);

      if (uErr) throw uErr;
      void publishShellEvent(SHELL_EVENTS.deviceUnpaired, {
        license_id: license.id,
        name: license.name,
      });
      loadLicenses();
    } catch (err) {
      console.error('Error unpairing device:', err);
    }
  };

  const deleteLicense = async (license: DeviceLicense) => {
    if (!confirm('Czy na pewno chcesz usunąć tę licencję?')) return;

    try {
      const { error: dErr } = await supabase.from('device_licenses').delete().eq('id', license.id);

      if (dErr) throw dErr;
      void publishShellEvent(SHELL_EVENTS.deviceRemoved, {
        license_id: license.id,
        name: license.name,
        modules: license.modules,
      });
      loadLicenses();
    } catch (err) {
      console.error('Error deleting license:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (license: DeviceLicense) => {
    if (license.device_id) {
      return (
        <span className="pill pill--active">
          <CheckCircle2 className="w-3 h-3" />
          Sparowana
        </span>
      );
    }
    if (license.status === 'active') {
      return (
        <span className="pill pill--pending">
          <Clock className="w-3 h-3" />
          Oczekuje
        </span>
      );
    }
    if (license.status === 'suspended') {
      return (
        <span className="pill pill--trial">
          <AlertCircle className="w-3 h-3" />
          Zawieszona
        </span>
      );
    }
    return (
      <span className="pill pill--error">
        <XCircle className="w-3 h-3" />
        Nieaktywna
      </span>
    );
  };

  const AVAILABLE_MODULES = [
    { id: 'waterandice', name: 'Temperatio', description: 'Kalkulator temperatury wody' },
    { id: 'lexshift', name: 'LexShift Mobile', description: 'Aplikacja mobilna dla pracowników' },
    {
      id: 'lexshift_kiosk',
      name: 'LexShift Kiosk',
      description: 'Urządzenie do rejestracji czasu',
    },
    {
      id: 'display',
      name: 'Ekran TV (żywa tablica)',
      description: 'Telewizor Android TV — dashboard Nexusa i Agendy',
    },
    // Panel produkcyjny jest urządzeniem klienta tak samo jak tablet czy
    // telewizor: ma stać na tej liście, dać się wyłączyć tym samym
    // przełącznikiem i wygasnąć razem z licencją. Klucz `fabrica_panel` czyta
    // sam panel przy parowaniu (`apps/fabrica-panel/lib/dane/parowanie.dart`)
    // i BEZ NIEGO ODMAWIA STARTU — licencja bez tego modułu jest licencją do
    // czegoś innego i panel mówi to od razu, zamiast pokazać pusty ekran.
    {
      id: 'fabrica_panel',
      name: 'Panel produkcyjny',
      description: 'Stanowisko naważania — waga, etapy, zapis partii',
    },
    // Aegis jest tu, bo TA lista jest jedynym miejscem, w którym moduł da się
    // włączyć na urządzeniu: `device_licenses.modules` wraca do telefonu przez
    // `mobile_worker_login` i puls sesji, a launcher Insolitum Mobile rysuje
    // dokładnie te klucze. Bez tego wpisu natywny ekran Aegisa był nieosiągalny —
    // nie dlatego, że go nie ma, tylko dlatego, że nie było jak go przyznać.
    {
      id: 'gamification',
      name: 'Aegis (moje wyniki)',
      description: 'Osobiste punkty, odznaki i ranking pracownika',
    },
    // FURNUS — hala przy piecu (organ INSOLITUM FURNUS). Klucz = `modules.id`
    // organu; ten sam czyta launcher Insolitum Mobile (`FURNUS_KEY`) i bramka
    // RPC hali (`furnus_mobile_context`, migracja 736), która dodatkowo wymaga
    // dostępu organizacji do organu. Tablet przy piecu dostaje ten moduł tutaj.
    {
      id: 'furnus',
      name: 'Furnus (hala pieca)',
      description: 'Komory, odliczanie wypieku, alarmy i plan nocy przy piecu',
    },
  ];

  /**
   * Jedna karta urządzenia — ten sam układ w każdej rodzinie. Wyciągnięta
   * z listy, bo listy są teraz trzy (telewizory, mobilne, stanowiska) i trzy
   * kopie tego samego JSX-a rozjechałyby się przy pierwszej poprawce.
   */
  const kartaLicencji = (license: DeviceLicense) => (
    // `id` jest kotwicą dla wiersza floty — kliknięcie w „waży na symulatorze"
    // ma dowieźć człowieka do karty TEGO stanowiska, a nie zostawić go
    // z przewijaniem listy w poszukiwaniu nazwy, którą właśnie przeczytał.
    <div key={license.id} id={`urzadzenie-${license.id}`} className="card p-4 scroll-mt-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-4">
          <span className="avatar w-12 h-12 rounded-ids-lg">
            <Smartphone className="w-6 h-6 text-aurora-on-accent" />
          </span>
          <div>
            <h3 className="text-aurora-text font-medium">
              {license.name || 'Urządzenie bez nazwy'}
            </h3>
            <p className="mono text-xs text-aurora-subtle mt-1 break-all">{license.license_code}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {getStatusBadge(license)}
              {license.modules.map((mod) => (
                <span key={mod} className="badge">
                  {mod}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => generatePairingCode(license)}
            disabled={isPairing}
            className="btn btn--tonal btn--sm"
          >
            <KeyRound className="w-4 h-4" />
            {license.device_id ? 'Nowy kod parowania' : 'Kod parowania'}
          </button>
          {license.device_id && (
            <button
              onClick={() => unpairDevice(license)}
              className="btn btn--sm bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
            >
              <Unlink className="w-4 h-4" />
              Odłącz
            </button>
          )}
          <button
            onClick={() => openEditModal(license)}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-aurora-text rounded-ids-md text-sm transition-colors"
            title="Edytuj licencję"
          >
            <Pencil className="w-4 h-4" />
            Edytuj
          </button>
          <button
            onClick={() => deleteLicense(license)}
            className="iconbtn w-8 h-8 hover:text-red-400"
            title="Usuń licencję"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="mt-4 pt-4 border-t border-aurora-border grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-aurora-subtle">Sparowane</p>
          <p className="mono text-aurora-muted">{formatDate(license.paired_at)}</p>
        </div>
        <div>
          <p className="text-aurora-subtle">Ostatnia aktywność</p>
          <p className="mono text-aurora-muted">{formatDate(license.last_seen_at)}</p>
        </div>
        <div>
          <p className="text-aurora-subtle">ID urządzenia</p>
          <p className="mono text-aurora-muted text-xs break-all">{license.device_id || '-'}</p>
        </div>
      </div>

      {/* What the device says about itself — the only way to diagnose a
          wall-mounted TV or a bench panel without standing in front of
          it. Two shapes, two cards, and the panel comes FIRST: a paired
          station can carry both keys (the wall runtime from an earlier
          life of the same licence), and its own telemetry is the one
          that describes what is standing there now. */}
      {license.device_info?.app === 'fabrica-panel' ? (
        <PanelDiagnostics
          licenseId={license.id}
          puls={license.device_info}
          heartbeatAt={license.last_heartbeat_at}
        />
      ) : (
        license.device_info?.runtime && (
          <DisplayDiagnostics
            runtime={license.device_info.runtime}
            heartbeatAt={license.last_heartbeat_at}
          />
        )
      )}
    </div>
  );

  const licencjeRodziny = (rodzina: RodzinaLicencji) =>
    licenses.filter((l) => rodzinaLicencji(l) === rodzina);

  const liczniki = policzRodziny(licenses);

  /**
   * Stanowiska w kształcie, którego chce flota. JEDNO miejsce, bo czytają je
   * dwa: kafel przeglądu (liczba usterek) i tablica w zakładce.
   */
  const stanowiskaDoOceny: StanowiskoDoOceny[] = licencjeRodziny('stanowiska').map((l) => ({
    id: l.id,
    nazwa: l.name,
    license_code: l.license_code,
    device_id: l.device_id,
    last_heartbeat_at: l.last_heartbeat_at,
    // Licencja z modułem, która nigdy się nie odezwała, nie ma pulsu — i to
    // jest inny stan niż puls pusty. Patrz `ocenStanowisko`.
    puls: l.device_info?.app === 'fabrica-panel' ? l.device_info : null,
  }));

  const kodNajnowszegoPanelu = najnowszyKodWersji(
    wydania,
    pozycjeRodziny('stanowiska').map((p) => p.klucz)
  );

  // Kafel przeglądu ma powiedzieć „ile jest zepsute", a nie tylko „ile jest".
  // Liczba licencji nie zmienia się miesiącami i dlatego przestaje być czytana.
  const flotaPodsumowanie = podsumujFlote(
    stanowiskaDoOceny.map((s) =>
      ocenStanowisko(s, { teraz: Date.now(), najnowszyKodWersji: kodNajnowszegoPanelu })
    )
  );

  /**
   * Lista urządzeń rodziny ze stanem pustym mówiącym, KTÓRA rodzina jest
   * pusta. „Brak urządzeń" pod zakładką Telewizory nie mówi człowiekowi nic —
   * a to zdanie ma podpowiedzieć następny ruch.
   */
  const listaRodziny = (
    rodzina: RodzinaLicencji,
    ikona: ReactNode,
    pusto: string,
    rada: string
  ) => {
    const lista = licencjeRodziny(rodzina);
    if (isLoading) {
      return (
        <div className="space-y-4">
          <div className="skel h-28" />
          <div className="skel h-28" />
        </div>
      );
    }
    if (lista.length === 0) {
      return (
        <EmptyState
          variant="card"
          icon={ikona}
          title={pusto}
          description={rada}
          action={
            <button
              onClick={() => {
                setError(null);
                setShowCreateModal(true);
              }}
              className="btn btn--primary btn--sm"
            >
              <Plus className="w-4 h-4" />
              Nowa licencja
            </button>
          }
        />
      );
    }
    return <div className="space-y-4">{lista.map(kartaLicencji)}</div>;
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Nagłówek działu. Trasa zostaje `/admin/devices` — ten adres jest
          zaszyty w mailach zdarzeń (`pocztaZdarzen/mapa.ts`), w nawigacji
          powłoki i w komunikacie błędu panelu produkcyjnego; zmiana adresu
          zerwałaby wszystkie trzy naraz. Zmienia się nazwa i wnętrze. */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <LayoutGrid className="w-6 h-6 text-aurora-brand" />
            Aplikacje i urządzenia
          </h1>
          <p className="pagehead__sub">
            Instalatory, licencje i stan sprzętu — telewizory, urządzenia mobilne, stanowiska i
            serwery wydruku
          </p>
        </div>
        <div className="pagehead__actions">
          <button onClick={loadLicenses} disabled={isLoading} className="iconbtn">
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              setError(null);
              setShowCreateModal(true);
            }}
            className="btn btn--primary"
          >
            <Plus className="w-4 h-4" />
            Nowa licencja
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="error" className="mb-6">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} aria-label="Zamknij komunikat">
            <XCircle className="w-4 h-4" />
          </button>
        </Alert>
      )}

      {/* Zakładka żyje w URL — tak samo jak w dziale Drukowanie. Dzięki temu
          da się podesłać komuś link „zobacz stanowiska", a odświeżenie strony
          nie wraca na przegląd. */}
      <Tabs value={zakladka} onValueChange={(v) => ustawZakladke(v)}>
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="przeglad" icon={<LayoutGrid className="w-4 h-4" />}>
            Przegląd
          </TabsTrigger>
          <TabsTrigger value="telewizory" icon={<Tv className="w-4 h-4" />}>
            Telewizory
          </TabsTrigger>
          <TabsTrigger value="mobilne" icon={<TabletSmartphone className="w-4 h-4" />}>
            Urządzenia mobilne
          </TabsTrigger>
          <TabsTrigger value="stanowiska" icon={<Factory className="w-4 h-4" />}>
            Stanowiska
          </TabsTrigger>
          <TabsTrigger value="serwery" icon={<Server className="w-4 h-4" />}>
            Serwery wydruku
          </TabsTrigger>
          <TabsTrigger value="integracje" icon={<Plug className="w-4 h-4" />}>
            Integracje
          </TabsTrigger>
        </TabsList>

        {/* ── Przegląd ───────────────────────────────────────────────────── */}
        <TabsContent value="przeglad">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {(
              [
                {
                  rodzina: 'telewizory' as const,
                  ikona: <Tv className="w-5 h-5" />,
                  nazwa: 'Telewizory i ściany',
                  pozycje: telewizory,
                  zakladka: 'telewizory',
                },
                {
                  rodzina: 'mobilne' as const,
                  ikona: <TabletSmartphone className="w-5 h-5" />,
                  nazwa: 'Tablety i telefony',
                  pozycje: mobilne,
                  zakladka: 'mobilne',
                },
                {
                  rodzina: 'stanowiska' as const,
                  ikona: <Factory className="w-5 h-5" />,
                  nazwa: 'Stanowiska produkcyjne',
                  pozycje: stanowiska,
                  zakladka: 'stanowiska',
                },
              ] as const
            ).map((k) => {
              const wersja = wersjaRodziny(k.pozycje);
              return (
                <button
                  key={k.rodzina}
                  onClick={() => ustawZakladke(k.zakladka)}
                  className="card card--interactive p-4 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-ids-md bg-aurora-brand/15 text-aurora-brand flex items-center justify-center">
                      {k.ikona}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-aurora-text truncate">{k.nazwa}</p>
                      <p className="text-xs text-aurora-subtle">
                        {wersja?.version_name
                          ? `Aplikacja ${wersja.version_name}`
                          : 'Brak opublikowanego wydania'}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 mono text-2xl text-aurora-text">{liczniki[k.rodzina]}</p>
                  <p className="text-xs text-aurora-subtle">
                    {liczniki[k.rodzina] === 1 ? 'licencja' : 'licencji'}
                  </p>
                  {/* Tylko stanowiska mają dziś werdykt — reszta rodzin nie
                      przysyła telemetrii, z której dałoby się go policzyć.
                      Kafel bez tej linijki mówi „ile jest", a to liczba, która
                      nie zmienia się miesiącami i dlatego przestaje być
                      czytana. */}
                  {k.rodzina === 'stanowiska' && liczniki.stanowiska > 0 && (
                    <p className="text-xs mt-1">
                      {flotaPodsumowanie.usterka > 0 ? (
                        <span className="text-red-400">
                          {flotaPodsumowanie.usterka}{' '}
                          {flotaPodsumowanie.usterka === 1 ? 'z usterką' : 'z usterkami'}
                        </span>
                      ) : flotaPodsumowanie.uwaga > 0 ? (
                        <span className="text-amber-400">
                          {flotaPodsumowanie.uwaga} do sprawdzenia
                        </span>
                      ) : (
                        <span className="text-emerald-400">wszystkie sprawne</span>
                      )}
                    </p>
                  )}
                </button>
              );
            })}

            {/* Serwery wydruku liczą się z innej kartoteki (`print_agents`),
                więc kafel nie pokazuje liczby — prowadzi do zakładki, która
                ma prawdę. */}
            <button
              onClick={() => ustawZakladke('serwery')}
              className="card card--interactive p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-ids-md bg-aurora-brand/15 text-aurora-brand flex items-center justify-center">
                  <Server className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-aurora-text truncate">Serwery wydruku</p>
                  <p className="text-xs text-aurora-subtle">
                    {wersjaRodziny(serwery)?.version_name
                      ? `Aplikacja ${wersjaRodziny(serwery)?.version_name}`
                      : 'Brak opublikowanego wydania'}
                  </p>
                </div>
              </div>
              <p className="mt-3 text-sm text-aurora-muted">
                Windows i Linux — maszyna, która drukuje w sieci lokalnej
              </p>
            </button>
          </div>

          <PrzewodnikParowania
            otwarty={showActivationGuide}
            przelacz={() => setShowActivationGuide(!showActivationGuide)}
          />
        </TabsContent>

        {/* ── Telewizory ─────────────────────────────────────────────────── */}
        <TabsContent value="telewizory">
          <KartaAplikacji
            ikona={<Tv className="w-5 h-5" />}
            tytul="Aplikacja na telewizor — żywa tablica (Android TV)"
            wersja={wersjaRodziny(telewizory)}
            pozycje={telewizory}
            urlQr={urlKoduQr(telewizory)}
            tytulQr="Kod QR do pobrania aplikacji Insolitum Universe na TV"
            naglowekInstrukcji="Instalacja na telewizorze"
            komunikatBrak="Opublikuj wydanie „universe-tv”, aby udostępnić instalator"
          >
            <p className="mt-1 text-xs text-aurora-muted">
              Pobierz APK i zainstaluj go na telewizorze z Android TV (sideload przez USB / ADB albo
              przeglądarkę TV). Po instalacji uruchom aplikację — pokaże ekran parowania.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Utwórz licencję urządzenia z modułem{' '}
              <span className="text-aurora-muted">Ekran TV</span> i wpisz 6-cyfrowy kod parowania
              pilotem. Kolejne aktualizacje aplikacja pobierze automatycznie (OTA).
            </p>
          </KartaAplikacji>

          {listaRodziny(
            'telewizory',
            <Tv className="w-8 h-8" />,
            'Brak telewizorów',
            'Dodaj licencję z modułem „Ekran TV", żeby sparować pierwszą żywą tablicę.'
          )}
        </TabsContent>

        {/* ── Urządzenia mobilne ─────────────────────────────────────────── */}
        <TabsContent value="mobilne">
          <KartaAplikacji
            ikona={<PackageCheck className="w-5 h-5" />}
            tytul="Aplikacja Insolitum Universe (Android)"
            wersja={wersjaRodziny(mobilne)}
            pozycje={mobilne}
            urlQr={urlKoduQr(mobilne)}
            tytulQr="Kod QR do pobrania aplikacji Insolitum Universe"
            naglowekInstrukcji="Zeskanuj tabletem"
            komunikatBrak="Opublikuj wydanie, aby udostępnić instalator"
          >
            <p className="mt-1 text-xs text-aurora-muted">
              Zeskanuj ten kod QR aparatem tabletu, aby pobrać instalator aplikacji bezpośrednio na
              urządzenie — bez przepisywania adresu.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Zainstaluj pobrany plik (zezwól na instalację z tego źródła). Kolejne aktualizacje
              aplikacja pobierze automatycznie.
            </p>
          </KartaAplikacji>

          {listaRodziny(
            'mobilne',
            <Smartphone className="w-8 h-8" />,
            'Brak urządzeń mobilnych',
            'Kliknij „Nowa licencja", aby dodać pierwszy tablet albo telefon pracownika.'
          )}
        </TabsContent>

        {/* ── Stanowiska produkcyjne ─────────────────────────────────────── */}
        <TabsContent value="stanowiska">
          {/* FLOTA STOI PRZED INSTALATOREM, bo pierwsze pytanie w tej zakładce
              brzmi „czy stanowiska działają", a nie „skąd pobrać paczkę".
              Tabela nie kosztuje ani jednego zapytania — ocenia licencje,
              które lista i tak już ma w pamięci. */}
          <div className="card p-4 mb-6">
            <h2 className="text-aurora-text font-medium mb-1 flex items-center gap-2">
              <Factory className="w-4 h-4 text-aurora-brand" />
              Flota stanowisk
            </h2>
            <p className="text-xs text-aurora-subtle mb-4">
              Najgorsze na górze. Wiersz mówi, co jest zepsute — szczegóły są na karcie urządzenia
              niżej.
            </p>
            <FlotaPaneli
              stanowiska={stanowiskaDoOceny}
              najnowszyKodWersji={kodNajnowszegoPanelu}
              laduje={isLoading}
              naWybor={(s) =>
                document
                  .getElementById(`urzadzenie-${s.id}`)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            />
          </div>

          <KartaAplikacji
            ikona={<Scale className="w-5 h-5" />}
            tytul="Panel produkcyjny — stanowisko naważania"
            wersja={wersjaRodziny(stanowiska)}
            pozycje={stanowiska}
            urlQr={urlKoduQr(stanowiska)}
            tytulQr="Kod QR do pobrania panelu produkcyjnego"
            naglowekInstrukcji="Instalacja na stanowisku"
            komunikatBrak="Opublikuj wydanie „fabrica-panel”, aby udostępnić instalator"
          >
            <p className="mt-1 text-xs text-aurora-muted">
              Zeskanuj kod tabletem stanowiskowym albo pobierz paczkę na peceta / Raspberry Pi. Po
              instalacji uruchom panel — pokaże ekran parowania z klawiaturą 6-cyfrową.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Utwórz licencję urządzenia z modułem{' '}
              <span className="text-aurora-muted">Panel produkcyjny</span> i wpisz kod na
              stanowisku. Panel zapyta też,{' '}
              <span className="text-aurora-muted">na jakim ekranie stoi</span> — z tej odpowiedzi
              wychodzi wielkość liter i przycisków w milimetrach, więc warto wskazać właściwą
              przekątną.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Każda platforma to{' '}
              <span className="text-aurora-muted">jeden plik, który instaluje</span>. Na Windows
              uruchom instalator (zawiera biblioteki MSVC, więc czysty pecet bez internetu
              wystarczy). Na Linuksie i Raspberry Pi:{' '}
              <span className="mono text-aurora-muted">sudo ./fabrica-panel-….run</span>. Wersja dla
              Pi jest budowana na tej samej glibc, co Raspberry Pi OS.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Instalator domyślnie włącza{' '}
              <span className="text-aurora-muted">tryb stanowiskowy</span>: panel wstaje przy każdym
              zalogowaniu, zajmuje cały ekran i nie pozwala pecetowi zgasić monitora. Żeby tego nie
              zakładać, odznacz to w kreatorze na Windows albo dodaj{' '}
              <span className="mono text-aurora-muted">-- --bez-autostartu</span> na Linuksie. Na
              tablecie panel można wskazać jako ekran główny (Ustawienia → Aplikacja ekranu
              głównego).
            </p>
          </KartaAplikacji>

          {listaRodziny(
            'stanowiska',
            <Factory className="w-8 h-8" />,
            'Brak stanowisk produkcyjnych',
            'Dodaj licencję z modułem „Panel produkcyjny", żeby sparować pierwsze stanowisko.'
          )}
        </TabsContent>

        {/* ── Serwery wydruku ────────────────────────────────────────────── */}
        <TabsContent value="serwery">
          <KartaAplikacji
            ikona={<Server className="w-5 h-5" />}
            tytul="Insolitum Cloud Print (Windows / Linux)"
            wersja={wersjaRodziny(serwery)}
            pozycje={serwery}
            urlQr={null}
            tytulQr=""
            naglowekInstrukcji="Instalacja na maszynie w sieci lokalnej"
            komunikatBrak="Opublikuj wydanie „cloud-print”, aby udostępnić instalator"
          >
            <p className="mt-1 text-xs text-aurora-muted">
              Serwer wydruku stoi na maszynie, która widzi drukarki w sieci lokalnej. Chmura nigdy
              nie łączy się do niego — to on odbiera zadania, więc nie trzeba otwierać żadnego portu
              na zewnątrz.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Instalator o nic nie pyta. Po instalacji Cloud Print startuje sam, siada{' '}
              <span className="text-aurora-muted">ikoną w zasobniku systemowym</span> i otwiera okno
              z prośbą o sześciocyfrowy kod parowania — adresu serwera nie podaje się nigdzie,
              aplikacja zna go sama. Kod jest jednorazowy i wygasa po 15 minutach; wygenerujesz go w
              dziale{' '}
              <Link to="/admin/printing?tab=agents" className="text-aurora-brand hover:underline">
                Drukowanie → Serwery druku
              </Link>
              . Kolor ikony mówi, co się dzieje: zielona drukuje, bursztynowa straciła łączność,
              szara czeka na parowanie.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Zamknięcie okna nie zatrzymuje druku — aplikacja chodzi dalej w zasobniku i po
              ponownym uruchomieniu maszyny wstaje sama. Kolejne wersje pobiera i instaluje bez
              pytania. Instalator Windows sprząta przy okazji po serwerze wydruku 0.3.x: zatrzymuje
              starą usługę i wyrejestrowuje ją, żeby dwa agenty nie pobierały zadań z tej samej
              kolejki.
            </p>
          </KartaAplikacji>

          <SerweryDruku />
        </TabsContent>

        {/* ── Integracje ─────────────────────────────────────────────────── */}
        <TabsContent value="integracje">
          <KartaAplikacji
            ikona={<Plug className="w-5 h-5" />}
            tytul="Integracja Receptor 3.0 Ready (Windows / Linux)"
            wersja={wersjaRodziny(integracje)}
            pozycje={integracje}
            urlQr={null}
            tytulQr=""
            naglowekInstrukcji="Instalacja na serwerze z Receptorem"
            komunikatBrak="Opublikuj wydanie „receptor-agent”, aby udostępnić instalator"
          >
            <p className="mt-1 text-xs text-aurora-muted">
              Agent stoi na tym samym serwerze co Receptor i jego baza, więc rozmawia z nimi po
              sieci lokalnej. Na zewnątrz wychodzi tylko on — chmura nigdy nie łączy się do środka,
              więc nie trzeba otwierać żadnego portu ani wystawiać Receptora do internetu.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Po instalacji agent prowadzi kreatorem krok po kroku: sześciocyfrowy kod parowania
              (adresu chmury nie podaje się nigdzie, aplikacja zna go sama; kod jest jednorazowy i
              wygasa po 15 minutach), potem adres API Receptora z kontem administratora i parametry
              bazy. Każdy krok kończy się sprawdzeniem — od razu widać, czy się udało, a jeśli nie,
              to dlaczego. Hasło administratora służy wyłącznie do jednorazowego pobrania klucza
              dostępu integracji i nie jest nigdzie zapisywane.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Zamknięcie okna nie zatrzymuje integracji — agent chodzi dalej w tle. Na Windows po
              ponownym uruchomieniu serwera wstaje sam tylko wtedy, gdy w agencie zaznaczysz
              „Uruchamiaj po zalogowaniu tego użytkownika Windows” (w agencie starszym niż 0.6.0:
              „Uruchamiaj przy starcie systemu”), i dopiero po zalogowaniu się na serwer. Na
              Linuksie agent nie wstaje sam po restarcie — trzeba go uruchomić ręcznie.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Nowe wersje agent pobiera i instaluje sam: zwykle w ciągu dwóch godzin od wydania
              kończy bieżącą pracę, instaluje się i uruchamia ponownie — przerwa trwa kilka minut.
              Na Windows wymaga to pełnych uprawnień administratora (na przykład wbudowanego konta
              Administrator); bez nich okno agenta pokaże „kliknij Zainstaluj i zrestartuj”. Na
              Linuksie i w agencie starszym niż 0.5.10 nowe wersje instalujesz tym przyciskiem w
              oknie agenta.
            </p>
            <p className="mt-2 text-xs text-aurora-subtle">
              Agent Integracji jest licencjonowany per serwer — liczbę serwerów i okres licencji
              ustala opiekun. Stan licencji widać poniżej, przy liście agentów.
            </p>
          </KartaAplikacji>

          <AgenciReceptora />
        </TabsContent>
      </Tabs>

      {/* Create License Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60">
          <div className="bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <h3 className="display text-lg font-semibold text-aurora-text mb-4">Nowa licencja</h3>

            <div className="space-y-4">
              <div className="field">
                <label className="field__label">Nazwa urządzenia</label>
                <input
                  type="text"
                  value={newLicenseName}
                  onChange={(e) => setNewLicenseName(e.target.value)}
                  placeholder="np. Tablet Produkcja 1"
                  className="input"
                />
              </div>

              <div className="field">
                <label className="field__label">Moduły</label>
                <div className="space-y-2">
                  {AVAILABLE_MODULES.map((mod) => (
                    <label
                      key={mod.id}
                      className="flex items-center gap-3 p-3 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md cursor-pointer hover:border-aurora-border-strong"
                    >
                      <input
                        type="checkbox"
                        checked={newLicenseModules.includes(mod.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewLicenseModules([...newLicenseModules, mod.id]);
                          } else {
                            setNewLicenseModules(newLicenseModules.filter((m) => m !== mod.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-aurora-border-strong text-blue-500 focus:ring-blue-500 bg-aurora-bg-subtle"
                      />
                      <div>
                        <span className="text-aurora-text">{mod.name}</span>
                        <p className="text-xs text-aurora-muted">{mod.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn--secondary flex-1"
              >
                Anuluj
              </button>
              <button
                onClick={createLicense}
                disabled={isCreating || !newLicenseName}
                className="btn btn--primary flex-1"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Tworzenie...
                  </>
                ) : (
                  'Utwórz'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit License Modal */}
      {editingLicense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60">
          <div className="bg-gray-800 rounded-ids-xl border border-gray-700 p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-aurora-text mb-1">Edytuj licencję</h3>
            <p className="text-xs text-gray-500 font-mono mb-4">{editingLicense.license_code}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nazwa urządzenia
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="np. Tablet Produkcja 1"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-ids-lg text-aurora-text placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Moduły</label>
                <div className="space-y-2">
                  {AVAILABLE_MODULES.map((mod) => (
                    <label
                      key={mod.id}
                      className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-ids-md cursor-pointer hover:bg-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={editModules.includes(mod.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditModules([...editModules, mod.id]);
                          } else {
                            setEditModules(editModules.filter((m) => m !== mod.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                      />
                      <div>
                        <span className="text-aurora-text">{mod.name}</span>
                        <p className="text-xs text-gray-400">{mod.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-ids-lg text-aurora-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Aktywna</option>
                    <option value="suspended">Zawieszona</option>
                    <option value="expired">Nieaktywna</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Wygasa (opcjonalnie)
                  </label>
                  <input
                    type="date"
                    value={editExpiresAt}
                    onChange={(e) => setEditExpiresAt(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-ids-lg text-aurora-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Zawieszenie lub dezaktywacja licencji zablokuje dostęp urządzenia do modułów.
                Parowanie urządzenia pozostaje bez zmian.
              </p>

              {/* Ustawienia ściany — tylko dla licencji z modułem `display`.
                  Dla tabletu czy kiosku ta sekcja nie ma sensu i nie ma jej. */}
              {editModules.includes('display') && (
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Czujniki na ekranie
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Nic nie zaznaczone = wszystkie. Zaznaczone pokazują się w kolejności z tej
                    listy. Pogodę ekran bierze z adresu organizacji.
                  </p>
                  {orgSensors.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Brak czujników w tej organizacji — ekran pokaże sam zegar i pogodę.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {orgSensors.map((sensor) => (
                        <label
                          key={sensor.device_id}
                          className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-ids-md cursor-pointer hover:bg-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={editSensors.includes(sensor.device_id)}
                            onChange={(e) => {
                              setEditSensors(
                                e.target.checked
                                  ? [...editSensors, sensor.device_id]
                                  : editSensors.filter((id) => id !== sensor.device_id)
                              );
                            }}
                            className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                          />
                          <span className="text-aurora-text text-sm">{sensor.device_name}</span>
                          <span className="ml-auto text-xs text-gray-500 font-mono">
                            {sensor.device_id}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {editSensors.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setEditSensors([])}
                      className="mt-3 text-xs text-gray-400 hover:text-aurora-text underline"
                    >
                      Wyczyść wybór (pokaż wszystkie)
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingLicense(null)}
                className="flex-1 py-3 bg-gray-700 text-aurora-text rounded-ids-lg hover:bg-gray-600 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={updateLicense}
                disabled={isSaving || !editName}
                className="flex-1 py-3 bg-blue-500 text-aurora-on-accent rounded-ids-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Zapisywanie...
                  </>
                ) : (
                  'Zapisz zmiany'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pairing Code Modal */}
      {pairing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60">
          <div className="bg-gray-800 rounded-ids-xl border border-gray-700 p-6 w-full max-w-xl text-center">
            <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-blue-400" />
            </div>

            <h3 className="text-lg font-semibold text-aurora-text mb-1">Kod parowania</h3>
            <p className="text-gray-400 text-sm mb-4">{pairing.license.name || 'Urządzenie'}</p>

            <div className="p-4 bg-gray-900 rounded-ids-lg mb-2">
              <p className="text-4xl font-mono font-bold text-blue-400 tracking-[0.3em]">
                {pairing.code}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Ważny do: {formatDate(pairing.expires_at)}
              </p>
            </div>
            <button
              onClick={() => copyToClipboard(pairing.code)}
              className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 mb-4"
            >
              <Copy className="w-4 h-4" />
              {copied ? 'Skopiowano' : 'Kopiuj kod'}
            </button>

            <p className="text-xs text-gray-500 mb-4">
              Wpisz ten 6-cyfrowy kod na ekranie parowania w aplikacji na urządzeniu. Kod jest
              jednorazowy i wygasa po 15 minutach.
            </p>

            <button
              onClick={() => setPairing(null)}
              className="w-full py-3 bg-gray-700 text-aurora-text rounded-ids-lg hover:bg-gray-600 transition-colors"
            >
              Zamknij
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
